import React from 'react'
import styled from "styled-components"
import { connect } from "react-redux"
import { ResultCard } from "buoy-card"
import { AutoSuggest, ButtonLink, Paragraph } from "buoy-core"
import { colors, fontWeight, fontSize, constants, responsive } from "buoy-theme"
import { FormattedMessage } from "react-intl"
import { startOver } from "../../interview/actions"

const Result = ResultCard.withComponent("button").extend`
  margin-bottom: 12px;
  cursor: pointer;
  text-align: left;
  position: relative;
  box-shadow: none;

  :before {
    z-index: 0;
    content: "";
    box-shadow: ${constants.boxShadow};
    border-radius: ${constants.borderRadius};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    transform: scale(1);
    transition: transform 0.2s ease;
  }

  &:hover:before {
    transform: scale(1.01);
  }

  p {
    ${fontSize("bodyMain")};
    font-weight: ${fontWeight.light};
    margin-bottom: 4px;
    &.title {
      font-weight: ${fontWeight.regular};
      color: ${colors.primary};
    }
    &.title:first-letter {
      text-transform: capitalize;
    }
  }
`

const ResultsHeader = styled(Paragraph)`
  margin: 20px 0 12px 16px;
  ${responsive("margin-top", "20px", "20px", "20px", "20px", "10px")}

  b {
    font-weight: ${fontWeight.medium};
  }
`

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: min-content;
  position: relative;
`

const cardinalSymptoms = [
  "chill",
  "chills",
  "cough",
  "coughs",
  "coughing",
  "fever",
  "fevers",
  "feverish",
  "sore throat",
  "difficulty getting enough air"
]

class Search extends React.Component {
  constructor() {
    super()
    this.state = {
      common: []
    }
  }

  componentDidMount() {
    const input = document.getElementById('search-input')
    input.addEventListener("keydown", this.keyDownInput, false)
    if (this.props.autoActivateSearch) input.focus()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activeSuggestion !== this.props.activeSuggestion) {
      if (!this.suggestions || this.props.activeSuggestion < 0) return
      this.suggestions.scrollTop = this.suggestions.children[this.props.activeSuggestion].offsetTop
    }
  }

  componentWillUnmount(){
    const input = document.getElementById('search-input')
    input.removeEventListener("keydown", this.keyDownInput, false);
  }

  keyDownInput = e => {
    if (this.props.onKeyDown) {
      return this.props.onKeyDown(e)
    }

    let key = e.keyCode || e.which
    let active = this.props.activeSuggestion
    switch (key) {
      case 13: // Enter/Return
        e.preventDefault()
        e.stopPropagation()
        e.target.blur()
        if (active > -1) {
          let suggestiontext = this.props.suggestions[active].text
          e.target.value = suggestiontext
          this.props.submitSearch(suggestiontext)
        } else {
          this.props.submitSearch(e.target.value)
        }
        break
      case 38: // Arrow up
        e.preventDefault()
        e.stopPropagation()
        this.props.decrement()
        break
      case 40: // Arrown down
        e.preventDefault()
        e.stopPropagation()
        this.props.increment()
        break
      default:
        break
    }
  }

  onFocus = () => this.setState({ focused: true })

  onClear = () => {
    this.props.searchClear()
    setTimeout(function() {
      document.getElementById('search-input').scrollIntoView()
    }, 20);
    document.getElementById('search-input').focus()
  }

  render() {
    let ResultsDescription
    let searchInCardinalSymptoms
    if (this.props.value && this.props.results.items) {
      if (this.props.results.items.length > 0) {
        ResultsDescription = (
          <ResultsHeader>
            <b>Results for "{this.props.value}"</b>
          </ResultsHeader>
        )
      } else {
        ResultsDescription = (
          <ResultsHeader>
            Your search <b>"{this.props.value}"</b> did not match any
            symptoms for a {this.props.age.value}-
            {this.props.age.unit.slice(0, -1)}-old {this.props.sex}. Try
            searching another symptom.
          </ResultsHeader>
        )
      }

      if (this.props.cardinalOptions) {
        // notify user if they search for a previously denied cardinal symptom
        searchInCardinalSymptoms = cardinalSymptoms.includes(this.props.value.toLowerCase())
        if (searchInCardinalSymptoms) {
          ResultsDescription = (
            <ResultsHeader>
              Hmm – it looks like you left this symptom out earlier. <br />
              Please <ButtonLink onClick={this.props.startOver}>start over</ButtonLink> and  
              select <b>"{this.props.value}"</b> as one of your answers to the first question.
            </ResultsHeader>
          )
        }
      }
    }

    return (
      <React.Fragment>
        {this.props.showQuestion && this.props.focused && (
          <Paragraph margin="20px auto 4px">
            <FormattedMessage
              id="interview.questions.symptom"
              defaultMessage="What symptom is bothering you the most?"
            />
          </Paragraph>
        )}
        <AutoSuggest
          {...this.props}
          selected={0}
          onClear={this.onClear}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onCancel={this.props.cancelClick}
          setListRef={s => (this.suggestions = s)}
          setSuggestionRef={() => () => true}
          suggestionHeader="Common searches"
          showDefaults={this.props.showCommonSearches}
          showSuggestionHeader={this.props.showCommonSearches}
        />

        <ResultsContainer>
          {this.props.results.items && this.props.suggestions.length === 0 && (
            <React.Fragment>
              {ResultsDescription}
              {!searchInCardinalSymptoms && this.props.results.items.map((s, i) => (
                <Result
                  key={i}
                  onClick={() => this.props.resultClick(s.token, s.title)}
                >
                  <p className="title">{s.title}</p>
                  <p>{s.description}</p>
                </Result>
              ))}
            </React.Fragment>
          )}

          {this.props.results.status === 204 &&
            this.props.suggestions.length === 0 && (
              <ResultsHeader>
                Your search <b>"{this.props.value}"</b> did not match any
                symptoms. Try using more general terms and checking your
                spelling.
              </ResultsHeader>
            )}
        </ResultsContainer>
      </React.Fragment>
    )
  }
}

Search.defaultProps = {
  results: [],
  suggestions: []
}

const mapStateToProps = state => ({
  cardinalOptions: state.simpleFlow.cardinalEvidence.options,
})

const mapDispatchToProps = dispatch => ({
  startOver: () => dispatch(startOver(true)),
})

const SearchWrapper = connect(mapStateToProps, mapDispatchToProps)(Search)


export { SearchWrapper as default }


// WEBPACK FOOTER //
// ./assets/search/components/Search.js